/**
 * 頁 16 — 設定（後台；**只准管理員**，`ui-spec.md` §2 頁 16）
 *
 * 紅線（`rules §七 A5`）：費率／結算日／WhatsApp 設定**唔准寫死喺代碼** →
 *   呢頁係唯一改動入口，key 一律由 **`/api/settings` 白名單**（後端）把關。
 * ⚠️ 前端**唔重複**做驗證邏輯（範圍／格式）—— 後端驗完回 400 ＋ 訊息，前端照顯示。
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api } from '../lib/api';

type Kind = 'num' | 'text' | 'weekday' | 'phones' | 'date';

interface FieldDef {
  key: string;
  labelKey: string;
  kind: Kind;
  hintKey?: string;
}

/** 同 `functions/api/settings/index.ts` 嘅 `RULES` 一一對應（key 名係契約） */
const FIELDS: FieldDef[] = [
  { key: 'app.base_url', labelKey: 'settings.appBaseUrl', kind: 'text', hintKey: 'settings.hintBaseUrl' },
  { key: 'fee.handling_per_cbm', labelKey: 'settings.feeHandlingPerCbm', kind: 'num' },
  { key: 'fee.storage_per_cbm_week', labelKey: 'settings.feeStoragePerCbmWeek', kind: 'num' },
  { key: 'fee.settlement_weekday', labelKey: 'settings.settlementWeekday', kind: 'weekday' },
  {
    key: 'fee.first_storage_from',
    labelKey: 'settings.feeFirstStorageFrom',
    kind: 'date',
    hintKey: 'settings.hintFirstStorageFrom',
  },
  { key: 'wa.template_outbound', labelKey: 'settings.waTemplateOutbound', kind: 'text', hintKey: 'settings.hintTemplate' },
  { key: 'wa.template_picked', labelKey: 'settings.waTemplatePicked', kind: 'text', hintKey: 'settings.hintTemplatePicked' },
  { key: 'wa.template_lang', labelKey: 'settings.waTemplateLang', kind: 'text' },
  { key: 'wa.max_items_in_message', labelKey: 'settings.waMaxItemsInMessage', kind: 'num' },
  { key: 'wa.recipients', labelKey: 'settings.waRecipients', kind: 'phones', hintKey: 'settings.hintRecipients' },
  { key: 'wa.recipients_staff', labelKey: 'settings.waRecipientsStaff', kind: 'phones', hintKey: 'settings.hintRecipientsStaff' },
  { key: 'outbound.order_no_prefix', labelKey: 'settings.outboundOrderNoPrefix', kind: 'text' },
  { key: 'outbound.delivery_code_prefix', labelKey: 'settings.outboundDeliveryCodePrefix', kind: 'text' },
  { key: 'intake.batch_no_prefix', labelKey: 'settings.intakeBatchNoPrefix', kind: 'text' },
];

/** 電話類設定（DB 存 JSON 陣列，UI 用逗號分隔）—— 新增 key 要一齊加呢度 */
const PHONE_KEYS = new Set(['wa.recipients', 'wa.recipients_staff']);

/** 電話設定：DB 存 JSON 陣列（純數字），UI 用逗號分隔字串 */
const phonesToCsv = (key: string, raw: string): string => {
  if (!PHONE_KEYS.has(key)) return raw;
  try {
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? arr.join(', ') : '';
  } catch {
    return '';
  }
};

const csvToPhones = (csv: string): string =>
  JSON.stringify(
    csv
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== ''),
  );

export default function Settings() {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.settings();
      const next: Record<string, string> = {};
      for (const row of res.settings ?? []) {
        next[row.key] = phonesToCsv(row.key, row.value);
      }
      setValues(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const patch: Record<string, string> = {};
      for (const f of FIELDS) patch[f.key] = f.kind === 'phones' ? csvToPhones(values[f.key] ?? '') : (values[f.key] ?? '');
      const res = await api.saveSettings(patch);
      setValues((v) => {
        const next = { ...v };
        for (const row of res.settings ?? []) {
          next[row.key] = phonesToCsv(row.key, row.value);
        }
        return next;
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('settings.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const weekdayOptions = [1, 2, 3, 4, 5, 6, 7].map((d) => ({
    value: String(d),
    label: t(`settings.weekday${d}`),
  }));

  return (
    <section style={{ ...card, padding: '16px' }}>
      <h2 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: 800 }}>{t('settings.title')}</h2>
      <p style={hintStyle}>{t('settings.note')}</p>

      {loading ? (
        <p style={hintStyle}>{t('common.loading')}</p>
      ) : (
        <form onSubmit={submit} style={{ display: 'grid', gap: '12px', marginTop: '8px' }}>
          {FIELDS.map((f) =>
            f.kind === 'weekday' ? (
              <FormSelectField
                key={f.key}
                id={`f-${f.key}`}
                labelKey={f.labelKey}
                value={values[f.key] ?? ''}
                onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                options={weekdayOptions}
              />
            ) : (
              <FormTextField
                key={f.key}
                id={`f-${f.key}`}
                labelKey={f.labelKey}
                type={f.kind === 'num' ? 'number' : f.kind === 'date' ? 'date' : 'text'}
                value={values[f.key] ?? ''}
                onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v ?? '' }))}
                hintKey={f.hintKey}
              />
            ),
          )}
          <div>
            <button type="submit" style={btnPrimary} disabled={busy}>
              {busy ? t('common.saving') : t('settings.save')}
            </button>
          </div>
          {error && (
            <p style={errText} role="alert">
              {error}
            </p>
          )}
          {saved && (
            <p style={{ ...hintStyle, color: 'var(--color-primary)', fontWeight: 700 }}>
              {t('settings.saved')}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
